import React, { useCallback, useMemo, useRef } from 'react';
import { IconUpload, Typography, DesignTokens } from '@klarna/bubble-ui';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { logEvent } from '@/utils/analytics';
import { LinkWithoutHref } from '../LinkWithoutHref';
import { ACCEPTED_FORMATS, ERROR_CODES, MAX_FILE_SIZE_IN_MB, MAX_NUMBER_OF_FILES, tPrefix, } from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import { UploadAreaLabel, UploaderContainer } from './styledComponents';
export const UploadArea = ({ t, documents = [], onUpload, cyElementId, errors, setErrors, }) => {
    const designContext = DesignTokens.useDesignContext();
    const fileInputRef = useRef(null);
    const openFileBrowser = useCallback(() => {
        var _a;
        // eslint-disable-next-line no-unused-expressions
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, [fileInputRef]);
    const removeDuplicateFiles = useCallback(files => {
        const uniqueFiles = files.reduce((acc, selectedFile) => documents.findIndex(doc => doc.filename === selectedFile.name) === -1
            ? [...acc, selectedFile]
            : acc, []);
        return {
            filteredFiles: uniqueFiles,
            error: uniqueFiles.length < files.length ? ERROR_CODES.DUPLICATE_FILE_NAME : undefined,
        };
    }, [documents]);
    const removeInvalidFileFormats = useCallback(selectedFiles => {
        const acceptedFiles = selectedFiles.filter(file => ACCEPTED_FORMATS.some(format => file.name.toUpperCase().endsWith(format)));
        return {
            filteredFiles: acceptedFiles,
            error: acceptedFiles.length < selectedFiles.length ? ERROR_CODES.INVALID_FILE_FORMAT : undefined,
        };
    }, [documents]);
    const removeTooManyFiles = useCallback(selectedFiles => {
        const maxNumberOfFilesToUpload = MAX_NUMBER_OF_FILES - documents.length;
        if (selectedFiles.length > maxNumberOfFilesToUpload) {
            logEvent(ANALYTICS_EVENTS.maxDocumentsAllowedReached, {
                numberOfDocumentsForUpload: selectedFiles.length,
            });
            return {
                filteredFiles: selectedFiles.slice(0, maxNumberOfFilesToUpload),
                error: ERROR_CODES.MAX_NUM_FILES_EXCEEDED,
            };
        }
        return { filteredFiles: selectedFiles };
    }, [documents]);
    const uploadFiles = useCallback((selectedFiles) => {
        setErrors(null);
        const { files: validSelectedFiles, errors } = [
            removeDuplicateFiles,
            removeInvalidFileFormats,
            removeTooManyFiles,
        ].reduce(({ files, errors }, filterFunc) => {
            const { filteredFiles, error } = filterFunc(files);
            return { files: filteredFiles, errors: error ? [...errors, error] : errors };
        }, { files: selectedFiles, errors: [] });
        if (errors.length) {
            setErrors(errors);
        }
        if (validSelectedFiles.length) {
            onUpload(validSelectedFiles);
        }
    }, [onUpload]);
    const canUploadMoreFiles = useMemo(() => documents.length < MAX_NUMBER_OF_FILES, [documents]);
    const onSelectFile = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const selectedFiles = Array.from(e.target.files || []);
        uploadFiles(selectedFiles);
    };
    return (React.createElement(UploaderContainer, { onDropFiles: uploadFiles, disabled: !canUploadMoreFiles, dragOverStyle: {
            backgroundColor: designContext
                .get('colors/backgrounds/accent-inline')
                .toString(),
        } },
        React.createElement(IconUpload, { colorToken: errors ? 'colors/text/neutral' : 'colors/text/default' }),
        React.createElement(UploadAreaLabel, null,
            React.createElement(Typography, { colorToken: "colors/text/neutral", textToken: "text-style/text/labels/body/regular" },
                t(`${tPrefix}.upload_area.title.before_link`),
                "\u00A0",
                React.createElement(LinkWithoutHref, { onPress: openFileBrowser, disabled: !canUploadMoreFiles, UNSAFE_props: { 'data-testid': 'file-browser-link' } }, t(`${tPrefix}.upload_area.title.link_text`))),
            React.createElement(Typography, { colorToken: "colors/text/neutral", textToken: "text-style/text/labels/micro/regular" }, t(`${tPrefix}.upload_area.description`, {
                acceptedFileFormats: ACCEPTED_FORMATS.join(' '),
                maxFileSizeMB: `${MAX_FILE_SIZE_IN_MB}`,
            }))),
        React.createElement("input", Object.assign({ "data-testid": "file-browser-input", type: "file", style: { display: 'none' }, ref: fileInputRef, 
            // @ts-expect-error: FIXME
            onChange: onSelectFile, multiple: true, accept: ACCEPTED_FORMATS.map(format => format.toLowerCase()).join(','), disabled: !canUploadMoreFiles }, generateCyElemId(cyElementId, window)))));
};
